import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { AnularPaqueteRequestSchema } from '@condo/contracts';
import type { AnularPaqueteRequest } from '@condo/contracts';
import { useAnularPaquete } from '@/lib/hooks/use-paquetes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface AnularDialogProps {
  paqueteId: string;
  destinatarioNombre: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AnularDialog({
  paqueteId,
  destinatarioNombre,
  open,
  onOpenChange,
}: AnularDialogProps) {
  const mutation = useAnularPaquete();

  const form = useForm<AnularPaqueteRequest>({
    resolver: zodResolver(AnularPaqueteRequestSchema),
    defaultValues: { razon: '' },
  });

  function handleClose(open: boolean) {
    if (!open) form.reset();
    onOpenChange(open);
  }

  async function onSubmit(values: AnularPaqueteRequest) {
    try {
      await mutation.mutateAsync({ id: paqueteId, body: values });
      toast.success('Paquete anulado', {
        description: `El registro del paquete de ${destinatarioNombre} fue anulado.`,
      });
      handleClose(false);
    } catch {
      toast.error('No se pudo anular el paquete');
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anular paquete</DialogTitle>
          <DialogDescription>
            Esta accion es irreversible. El registro del paquete de {destinatarioNombre} quedara anulado.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="razon-anular">Razon de anulacion</Label>
            <textarea
              id="razon-anular"
              rows={3}
              className="mt-1.5 flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]"
              placeholder="Ej: Ingreso erroneo, paquete no corresponde"
              {...form.register('razon')}
            />
            {form.formState.errors.razon && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.razon.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="danger" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Anulando…
                </>
              ) : (
                'Anular paquete'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
